import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  Job,
  CreateJob,
  UpdateJob,
  JobViewRequest,
  JobStats,
  JobAnalyticsResponse,
  BulkJobStatusUpdate,
  BulkJobDelete,
  BulkOperationResult,
  CreateEnhancedJobData,
  JobDetailedAnalytics,
} from '../../services/unified/types';
import type { PublicStats } from '../../services/unified/jobsModule';

/**
 * Query hook for getting all jobs
 */
export const useJobs = () => {
  return useQuery({
    queryKey: queryKeys.jobs.lists(),
    queryFn: async (): Promise<Job[]> => {
      const response = await unifiedApiService.jobs.getJobs();
      return response.jobs;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting a single job by ID
 */
export const useJob = (id: string) => {
  return useQuery({
    queryKey: queryKeys.jobs.detail(id),
    queryFn: (): Promise<Job> => {
      return unifiedApiService.jobs.getJobById(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting job statistics
 */
export const useJobStats = (jobId: string) => {
  return useQuery({
    queryKey: [...queryKeys.jobs.detail(jobId), 'stats'],
    queryFn: (): Promise<JobStats> => {
      return unifiedApiService.jobs.getJobStats(jobId);
    },
    enabled: !!jobId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting job analytics (admin)
 */
export const useJobAnalytics = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.analytics(), 'jobs'],
    queryFn: (): Promise<JobAnalyticsResponse> => {
      return unifiedApiService.jobs.getJobAnalytics();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mutation hook for tracking job views
 */
export const useTrackJobView = () => {
  return useMutation({
    mutationFn: ({ jobId, viewData }: { jobId: string; viewData: JobViewRequest }) => {
      return unifiedApiService.jobs.trackJobView(jobId, viewData);
    },
    // Don't show errors for view tracking
    onError: () => {
      // Silently fail
    },
  });
};

/**
 * Mutation hook for creating a job (admin)
 */
export const useCreateJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (jobData: CreateJob): Promise<Job> => {
      return unifiedApiService.jobs.createJob(jobData);
    },
    onSuccess: () => {
      // Invalidate jobs list to show new job
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
};

/**
 * Mutation hook for updating a job (admin) with optimistic updates
 */
export const useUpdateJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ jobId, jobData }: { jobId: string; jobData: UpdateJob }): Promise<Job> => {
      return unifiedApiService.jobs.updateJob(jobId, jobData);
    },
    // Optimistic update
    onMutate: async ({ jobId, jobData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.jobs.detail(jobId) });
      
      // Snapshot the previous value
      const previousJob = queryClient.getQueryData<Job>(queryKeys.jobs.detail(jobId));
      
      // Optimistically update to the new value
      if (previousJob) {
        queryClient.setQueryData<Job>(queryKeys.jobs.detail(jobId), {
          ...previousJob,
          ...jobData,
        });
      }
      
      // Return context with the snapshot
      return { previousJob };
    },
    onSuccess: (updatedJob, variables) => {
      // Update the specific job in cache with server response
      queryClient.setQueryData(queryKeys.jobs.detail(variables.jobId), updatedJob);
      
      // Invalidate jobs list to reflect changes
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
    },
    // Rollback on error
    onError: (_err, { jobId }, context) => {
      if (context?.previousJob) {
        queryClient.setQueryData(queryKeys.jobs.detail(jobId), context.previousJob);
      }
    },
  });
};

/**
 * Mutation hook for deleting a job (admin)
 */
export const useDeleteJob = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (jobId: string): Promise<void> => {
      await unifiedApiService.jobs.deleteJob(jobId);
    },
    onSuccess: (_, jobId) => {
      // Remove the job from cache
      queryClient.removeQueries({ queryKey: queryKeys.jobs.detail(jobId) });
      
      // Invalidate jobs list to reflect deletion
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
    },
  });
};

/**
 * Mutation hook for bulk updating job status (admin)
 */
export const useBulkUpdateJobStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: BulkJobStatusUpdate): Promise<BulkOperationResult> => {
      return unifiedApiService.jobs.bulkUpdateJobStatus(request);
    },
    onSuccess: () => {
      // Invalidate all jobs to reflect bulk changes
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
};

/**
 * Mutation hook for bulk deleting jobs (admin)
 */
export const useBulkDeleteJobs = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: BulkJobDelete): Promise<BulkOperationResult> => {
      return unifiedApiService.jobs.bulkDeleteJobs(request);
    },
    onSuccess: () => {
      // Invalidate all jobs to reflect bulk deletion
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
};

/**
 * Mutation hook for uploading job image (admin)
 */
export const useUploadJobImage = () => {
  return useMutation({
    mutationFn: ({
      file,
      type,
      jobId,
    }: {
      file: File;
      type: 'logo' | 'job-image';
      jobId?: string;
    }) => {
      return unifiedApiService.jobs.uploadJobImage(file, type, jobId);
    },
    onError: (error) => {
      console.error('Job image upload failed:', error);
    },
  });
};

/**
 * Mutation hook for deleting job image (admin)
 */
export const useDeleteJobImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (filename: string): Promise<{ message: string }> => {
      return unifiedApiService.jobs.deleteJobImage(filename);
    },
    onSuccess: () => {
      // Invalidate jobs cache to reflect image deletion
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
    onError: (error) => {
      console.error('Job image deletion failed:', error);
    },
  });
};

/**
 * Query hook for getting featured jobs
 */
export const useFeaturedJobs = () => {
  return useQuery({
    queryKey: queryKeys.jobs.featured(),
    queryFn: async (): Promise<Job[]> => {
      const response = await unifiedApiService.jobs.getFeaturedJobs();
      return response.jobs;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting job with detailed analytics
 */
export const useJobDetailedAnalytics = (jobId: string) => {
  return useQuery({
    queryKey: [...queryKeys.jobs.detail(jobId), 'detailed-analytics'],
    queryFn: (): Promise<JobDetailedAnalytics> => {
      return unifiedApiService.jobs.getJobDetailedAnalytics(jobId);
    },
    enabled: !!jobId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Mutation hook for updating job status
 */
export const useUpdateJobStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      jobId, 
      status, 
      notes 
    }: { 
      jobId: string; 
      status: 'draft' | 'active' | 'archived' | 'closed';
      notes?: string;
    }): Promise<Job> => {
      return unifiedApiService.jobs.updateJobStatus(jobId, status, notes);
    },
    onSuccess: (updatedJob, variables) => {
      // Update the specific job in cache with server response
      queryClient.setQueryData(queryKeys.jobs.detail(variables.jobId), updatedJob);
      
      // Invalidate jobs list to reflect status change
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      
      // Invalidate featured jobs if status changed
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.featured() });
    },
  });
};

/**
 * Mutation hook for toggling featured status
 */
export const useToggleFeaturedStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (jobId: string): Promise<Job> => {
      return unifiedApiService.jobs.toggleFeaturedStatus(jobId);
    },
    onSuccess: (updatedJob, jobId) => {
      // Normalize is_featured to boolean for consistent UI handling
      const normalizedJob = {
        ...updatedJob,
        is_featured: Boolean(updatedJob.is_featured),
      };
      
      // Update the specific job in cache with server response
      queryClient.setQueryData(queryKeys.jobs.detail(jobId), normalizedJob);
      
      // Also invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(jobId) });
      
      // Invalidate jobs list to reflect featured status change
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.lists() });
      
      // Invalidate featured jobs list
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.featured() });
    },
  });
};

/**
 * Mutation hook for saving job as draft
 */
export const useSaveJobDraft = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (jobData: CreateEnhancedJobData): Promise<Job> => {
      return unifiedApiService.jobs.saveJobDraft(jobData);
    },
    onSuccess: () => {
      // Invalidate jobs list to show new draft
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
};

/**
 * Query hook for loading job draft
 */
export const useJobDraft = (draftId: string) => {
  return useQuery({
    queryKey: [...queryKeys.jobs.all, 'draft', draftId],
    queryFn: (): Promise<Job> => {
      return unifiedApiService.jobs.loadJobDraft(draftId);
    },
    enabled: !!draftId,
    staleTime: 0, // Always fetch fresh draft data
  });
};

/**
 * Query hook for getting public statistics (for home page)
 */
export const usePublicStats = () => {
  return useQuery({
    queryKey: ['public', 'stats'],
    queryFn: (): Promise<PublicStats> => {
      return unifiedApiService.jobs.getPublicStats();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};
